/*----------------------------------
- DOMAINS FILTER PRESETS (SSOT)
----------------------------------*/

import type { TInputRadar } from '@/common/types/domains/radar';

/*----------------------------------
- TYPES
----------------------------------*/

type RadarSort = NonNullable<TInputRadar['sort']>;

/*----------------------------------
- CATALOG
----------------------------------*/

export const DEFAULT_RADAR_SORT: RadarSort = { field: 'seoRandomKey', dir: 'asc' };

/**
 * Presets are expressed as Radar queries so they work end-to-end:
 * - The client can render them as applied rules.
 * - The backend can convert them to SQL via Radar builders.
 */
export const filterPresets = {
    short: {
        sort: DEFAULT_RADAR_SORT,
        rules: {
            op: 'AND',
            rules: [{ filterId: 'length_chars', field: 'length', op: '<=', value: 6 }],
        },
    },
    brandable: {
        sort: DEFAULT_RADAR_SORT,
        rules: {
            op: 'AND',
            rules: [{ filterId: 'brandability', field: 'wordBrandability', op: '>=', value: 80 }],
        },
    },
    positive: {
        sort: DEFAULT_RADAR_SORT,
        rules: {
            op: 'AND',
            rules: [{ filterId: 'sentiment', field: 'wordSentiment', op: '>=', value: 60 }],
        },
    },
    hacks: {
        sort: DEFAULT_RADAR_SORT,
        rules: {
            op: 'AND',
            rules: [{ filterId: 'format', field: 'format', op: 'IN', value: 'hack' }],
        },
    },
    actionable: {
        sort: DEFAULT_RADAR_SORT,
        rules: {
            op: 'AND',
            rules: [{ filterId: 'part_of_speech', field: 'wordPos', op: 'IN', value: ['verb'] }],
        },
    },
    expiring: {
        sort: DEFAULT_RADAR_SORT,
        rules: {
            op: 'AND',
            rules: [{ filterId: 'status', field: 'status', op: 'IN', value: ['expiring'] }],
        },
    },
    expired: {
        sort: DEFAULT_RADAR_SORT,
        rules: {
            op: 'AND',
            rules: [{ filterId: 'status', field: 'status', op: 'IN', value: ['expired'] }],
        },
    },
} satisfies Record<string, TInputRadar>;

export type FilterPresetName = keyof typeof filterPresets;

export type PopularFilterPresetCatalogItem = {
    presetName: FilterPresetName;
    label: string;
    description: string;
    iconName: 'sparkles' | 'tag' | 'arrow-up-right' | 'globe' | 'clock';
};

// The "Lists" column of the Domains menu (desktop megamenu and phone accordion).
// Two to four words each, per the 2026-09 mockup: the row is scanned, not read, and
// the label already carries most of the meaning.
export const DOMAINS_POPULAR_FILTER_PRESET_CATALOG: readonly PopularFilterPresetCatalogItem[] = [
    {
        presetName: 'brandable',
        label: 'Brandable',
        description: 'Reads like a brand',
        iconName: 'sparkles',
    },
    {
        presetName: 'short',
        label: 'Short',
        description: 'Six letters or fewer',
        iconName: 'tag',
    },
    {
        presetName: 'actionable',
        label: 'Actionable',
        description: 'Verbs, direct words',
        iconName: 'arrow-up-right',
    },
    {
        presetName: 'hacks',
        label: 'Domain hacks',
        description: 'The ending finishes the word',
        iconName: 'globe',
    },
    {
        presetName: 'expiring',
        label: 'Expiring',
        description: 'About to drop',
        iconName: 'clock',
    },
] as const;

export const getPublicFilterPresetHref = (presetName: FilterPresetName): string => {
    return `/domains/${encodeURIComponent(presetName)}`;
};
